import { type FocusEvent, useState } from 'react';
import { useAppleLoginFlag } from '~/hooks/useAppleLoginFlag';
import { useFacebookLoginFlag } from '~/hooks/useFacebookLoginFlag';
import { useSocialLoginFlag } from '~/hooks/useSocialLoginFlag';
import { OnboardingButton } from '~/pages/auth/onboarding/components/OnboardingButton/OnboardingButton';
import { OnboardingInput } from '~/pages/auth/onboarding/components/OnboardingInput/OnboardingInput';
import {
  SocialLoginButton,
  type SocialProvider,
} from '~/pages/auth/onboarding/components/SocialLoginButton/SocialLoginButton';

/**
 * Props for the EmailAndSocialAuth component
 */
interface EmailAndSocialAuthProps {
  /**
   * Email value from the form
   */
  email: string;

  /**
   * Callback when email value changes
   */
  onEmailChange: (value: string) => void;

  /**
   * Callback when email input is blurred
   */
  onEmailBlur: (e: FocusEvent<HTMLInputElement>) => void;

  /**
   * Whether the form is currently submitting
   */
  isSubmitting: boolean;

  /**
   * Email validation error message
   */
  emailError?: string;

  /**
   * Whether the email field has been touched
   */
  emailTouched?: boolean;

  /**
   * Callback when the Continue button is clicked
   */
  onContinue: () => void;

  /**
   * Callback when a social login button is clicked
   */
  onSocialLogin: (provider: SocialProvider) => void;

  /**
   * Email input placeholder
   * @default "Enter Email Address"
   */
  emailPlaceholder?: string;

  /**
   * Action type for social buttons (login, signup, or continue)
   * @default "continue"
   */
  socialAction?: 'login' | 'signup' | 'continue';
}

/**
 * Reusable component for email input + social login buttons
 *
 * This component provides the standard authentication UI pattern used across
 * login and registration flows: an email input field, a continue button,
 * an OR divider, and social login buttons for Google, Facebook, and Apple.
 *
 * Each social provider is individually feature-flagged:
 * - Google: `enable-social-login` (main social login flag)
 * - Facebook: `enable-social-login-facebook`
 * - Apple: `enable-social-login-apple`
 *
 * The OR divider and social section only render when at least one provider is enabled.
 *
 * @example
 * ```tsx
 * <EmailAndSocialAuth
 *   email={email}
 *   onEmailChange={(value) => setEmail(value)}
 *   onEmailBlur={handleBlur}
 *   isSubmitting={isSubmitting}
 *   emailError={errors.email}
 *   emailTouched={touched.email}
 *   onContinue={handleSubmit}
 *   onSocialLogin={handleSocialLogin}
 *   socialAction="login"
 * />
 * ```
 */
export const EmailAndSocialAuth = ({
  email,
  onEmailChange,
  onEmailBlur,
  isSubmitting,
  emailError,
  emailTouched,
  onContinue,
  onSocialLogin,
  emailPlaceholder = 'Enter Email Address',
  socialAction = 'continue',
}: EmailAndSocialAuthProps) => {
  const [socialLoading, setSocialLoading] = useState<Record<SocialProvider, boolean>>({
    google: false,
    apple: false,
    facebook: false,
  });

  // Feature flags for each social provider
  const { isSocialLoginEnabled: isGoogleEnabled } = useSocialLoginFlag();
  const { isFacebookLoginEnabled } = useFacebookLoginFlag();
  const { isAppleLoginEnabled } = useAppleLoginFlag();

  const hasAnySocialProvider = isGoogleEnabled || isFacebookLoginEnabled || isAppleLoginEnabled;

  const handleSocialClick = (provider: SocialProvider) => {
    // Set loading state for the specific provider - stays true as page will redirect
    setSocialLoading((prev) => ({ ...prev, [provider]: true }));

    // Call the parent handler (which triggers OAuth redirect)
    onSocialLogin(provider);
  };

  return (
    <div className="space-y-3">
      {/* Email Input */}
      <OnboardingInput
        id="email-input"
        type="email"
        name="email"
        label="Email"
        placeholder={emailPlaceholder}
        value={email}
        onChange={onEmailChange}
        onBlur={onEmailBlur}
        isDisabled={isSubmitting}
        isInvalid={Boolean(emailError && emailTouched)}
        error={emailError && emailTouched ? emailError : undefined}
        size="md"
      />

      {/* Continue Button */}
      <OnboardingButton
        type="submit"
        size="lg"
        isDisabled={isSubmitting || Boolean(emailError && emailTouched)}
        isLoading={isSubmitting}
        onClick={onContinue}
      >
        Continue
      </OnboardingButton>

      {/* OR Divider + Social Login Buttons - only shown when at least one provider is enabled */}
      {hasAnySocialProvider && (
        <>
          <div className="flex items-center gap-3 text-sm">
            <div className="flex-1 border-t border-disabled" />
            <span className="text-gray-400">OR</span>
            <div className="flex-1 border-t border-disabled" />
          </div>

          <div className="space-y-3">
            {isGoogleEnabled && (
              <SocialLoginButton
                provider="google"
                action={socialAction}
                type="button"
                onClick={() => handleSocialClick('google')}
                isLoading={socialLoading.google}
              />
            )}
            {isFacebookLoginEnabled && (
              <SocialLoginButton
                provider="facebook"
                action={socialAction}
                type="button"
                onClick={() => handleSocialClick('facebook')}
                isLoading={socialLoading.facebook}
              />
            )}
            {isAppleLoginEnabled && (
              <SocialLoginButton
                provider="apple"
                action={socialAction}
                type="button"
                onClick={() => handleSocialClick('apple')}
                isLoading={socialLoading.apple}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};
